import React, { useCallback, useMemo, useState, useRef, useEffect } from "react";
import { Typography } from "../../interfaces/typography";
import { useSessionDetails } from "../../lib/redux/sessions/data/data-hook";
import { TranscriptHeader } from "./TranscriptView/TranscriptHeader";
import { TranscriptBody } from "./TranscriptView/TranscriptBody";
import { AIGenerationSection } from "./AIGenerationSection";
import { CommentPanel } from "./TranscriptView/CommentPanel";
import { TranscriptAudio } from "./TranscriptView";
import "./MainView.css";
import { SEC_TO_MS } from "../../cfg/const";
import { SessionDetailView, viewCanSeeComments, viewCanSeeHeader } from "../History/views";
import { LoadingState } from "./Defaults/Loading";
import { NoDataState } from "./Defaults/NoDataState";

interface SessionDetailsProps {
  sessionId: string;
  view: SessionDetailView;
  inView?: boolean;
  onClose?: () => void;
}


export function SessionDetails({
  sessionId,
  view,
  inView=true,
  onClose
}: SessionDetailsProps) {
  const { sessionData, isLoading, refresh } = useSessionDetails({ sessionId });
  const [selectedTranscriptIdx, setSelectedTranscriptIdx] = useState<number | undefined>();
  const [currentTimeInMs, setCurrentTimeInMs] = useState(0);
  const currentTimeInMsRef = useRef(0);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isCommentPanelOpen, setIsCommentPanelOpen] = useState(false);
  const [speed, setSpeed] = useState(1); // Default to 1x (index 2)
  const [previousPlayState, setPreviousPlayState] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  const totalDurationInMs = useMemo(() => {
    return (sessionData?.call_metric?.call_duration ?? 0) * SEC_TO_MS
  }, [sessionData])

  useEffect(() => {
    if (selectedTranscriptIdx !== undefined && sessionData && sessionData.turns.length > selectedTranscriptIdx) {
      const transcript = sessionData?.turns[selectedTranscriptIdx]
      if (transcript) { 
        const timeInMs = transcript.start * SEC_TO_MS;
        setCurrentTimeInMs(timeInMs);
        // Also update the ref so play starts from the correct position
        currentTimeInMsRef.current = timeInMs;
      }
    }
  }, [selectedTranscriptIdx, sessionData])
  
  // Reset playback state when session data changes
  useEffect(() => {
    currentTimeInMsRef.current = 0;
    setCurrentTimeInMs(0);
    setIsPlaying(false);
    setSelectedTranscriptIdx(undefined);
  }, [sessionId, inView]);

  const handleUpdateTime = (time?: number, isActive?: boolean, prevActive?: boolean) => {
    if (time !== undefined) {
      currentTimeInMsRef.current = time;
      setCurrentTimeInMs(time);
    }
    setIsPlaying(isActive ?? false);
    setPreviousPlayState(prevActive ?? false);
  };

  const toggleCommentPanel = () => setIsCommentPanelOpen(!isCommentPanelOpen)

  // Timer effect for updating current time
  useEffect(() => {
    // Clear any existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Start a new timer if playing
    if (isPlaying) {
      timerRef.current = setInterval(() => {
        const newTime = currentTimeInMsRef.current + (speed * 250);
        if (newTime > totalDurationInMs) {
          setIsPlaying(false);
        }
        const clampedTime = Math.min(newTime, totalDurationInMs)
        currentTimeInMsRef.current = clampedTime
        setCurrentTimeInMs(clampedTime);
      }, 250);
    }

    // Cleanup function
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isPlaying, speed, totalDurationInMs]); // Re-create timer when isPlaying, speed, or duration changes

  if (isLoading) return <LoadingState />;
  if (!sessionData) return <NoDataState onRetry={refresh} />;

  return (
    <div className="w-full h-full flex flex-col bg-white">
      {viewCanSeeHeader(view) && <TranscriptHeader 
        data={sessionData} 
        onClose={onClose}
        view={view}
      />}
      
      {/* AI Generation Section */}
      <AIGenerationSection sessionData={sessionData} view={view} />
      
      <div className="flex-grow overflow-auto flex">
        <TranscriptBody 
          view={view}
          data={sessionData}
          currentTimeInMs={currentTimeInMs}
          isPlaying={isPlaying}
          updateSelectedTranscript={(idx) => {
            setSelectedTranscriptIdx(idx)
              if (isPlaying) {
                setIsPlaying(false);
              }
          }}
          selectedTranscriptIdx={selectedTranscriptIdx}
        />
      </div>

      {/* General Comments Panel */}
      {viewCanSeeComments(view) && sessionData && (
        <CommentPanel
          sessionId={sessionData.session.session_id}
          isOpen={isCommentPanelOpen}
          onToggle={toggleCommentPanel}
          onScrollToTranscript={setSelectedTranscriptIdx}
        />
      )}

      <TranscriptAudio
        data={sessionData}
        currentTimeInMs={currentTimeInMs}
        isPlaying={isPlaying}
        updateTime={handleUpdateTime}
        setSpeed={setSpeed}
        speed={speed}
        previousPlayState={previousPlayState}
        totalDuration={totalDurationInMs}
      />
    </div>
  );
}
